import { Text, Title } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { format, isFuture } from "date-fns";

import { useGetWorkerCredentials } from "./api/useGetWorkerCredentials";

interface ManageCredentialsModalProps {
  onClose: () => void;
  onReset: () => void;
  workerName: string | undefined;
  workerId: string;
  workplaceId: string;
}

const DEFAULT_EXPIRATION_DATE_STRING = new Date("2025-12-02");

export function ManageCredentialsModal({
  onClose,
  onReset,
  workerName,
  workerId,
  workplaceId,
}: ManageCredentialsModalProps) {
  const { data: workerCredentialsResponse } = useGetWorkerCredentials({
    filter: { workerIds: workerId, workplaceId },
  });

  const workerCredentials =
    workerCredentialsResponse?.data[0]?.attributes?.chartingCredentials ?? {};
  const {
    software = "PointClickCare",
    username,
    expiresAt = DEFAULT_EXPIRATION_DATE_STRING,
  } = workerCredentials;
  const manageCredentialsFor = isDefined(workerName) ? ` for ${workerName}` : "";
  const title = `${software} account${manageCredentialsFor}`;
  const workerCredential = {
    username:
      username ?? workerName?.toLowerCase().split(" ").join(".") ?? `cbh.${workerId.slice(0, 8)}`,
    expiresAt,
  };

  const isActive = isFuture(workerCredential.expiresAt);
  const statusText = isActive ? "Active" : "Inactive";
  const statusSubtext = "Inactive accounts will automatically be reactivated before shifts";
  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={() => {
        onClose();
      }}
    >
      <DialogTitle>
        <Stack direction="column" spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Title component="h2" variant="h3">
              {title}
            </Title>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="column" spacing={3}>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Text semibold variant="body1">
              Username
            </Text>
            <Text variant="body2">{workerCredential.username}</Text>
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Text semibold variant="body1">
              Status
            </Text>
            <Chip label={statusText} color={isActive ? "success" : "warning"} />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="top">
            <Stack direction="column">
              <Text semibold variant="body1">
                {statusText}
              </Text>
              <Text variant="body2">{statusSubtext}</Text>
            </Stack>
            <Text variant="body2">{format(workerCredential.expiresAt, "MM/dd/yyyy")}</Text>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            color: (theme) => theme.palette.text.primary,
            paddingX: 4,
            paddingY: 1,
          }}
          onClick={() => {
            onReset();
          }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          sx={{
            paddingX: 4,
            paddingY: 1,
          }}
          onClick={() => {
            onClose();
          }}
        >
          Okay
        </Button>
      </DialogActions>
    </Dialog>
  );
}
